"use client";
import Image from 'next/image';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight } from 'lucide-react';
import "@/scss/_category.scss";
import Energy from './../../../../public/icons/energy.png';
import { useParams } from 'next/navigation';
import { getCategoryListAPI } from '@/services/categoryPageService';
import Link from 'next/link';

const ITEMS_PER_PAGE = 9;

function getVisiblePages(current, last, width = 5) {
    if (last <= width) {
        return Array.from({ length: last }, (_, i) => i + 1);
    }
    const half = Math.floor(width / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(last, start + width - 1);
    if (end - start < width - 1) {
        start = Math.max(1, end - width + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default function CategoryPage() {
    const { id } = useParams();
    const [categoryData, setCategoryData] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [type, setType] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const skipFetchEffectRef = useRef(false);

    const fetchCategoryData = useCallback(
        async (typeArg, pageArg) => {
            if (!id) return;
            const response = await getCategoryListAPI({
                category: id,
                type: typeArg,
                per_page: ITEMS_PER_PAGE,
                page: pageArg,
            });
            setCategoryData(response.data);
            setPagination(response.pagination ?? null);
        },
        [id]
    );

    useLayoutEffect(() => {
        setCurrentPage(1);
    }, [id, type]);

    useEffect(() => {
        if (!id) return;
        if (skipFetchEffectRef.current) {
            skipFetchEffectRef.current = false;
            return;
        }
        fetchCategoryData(type, currentPage);
    }, [id, type, currentPage, fetchCategoryData]);

    const totalPages = pagination?.last_page ?? 1;
    const visiblePages = useMemo(
        () => getVisiblePages(currentPage, totalPages, 5),
        [currentPage, totalPages]
    );

    const goToPage = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    return (
        <div className="category-page">
            {/* Hero Section */}
            <section className="category-hero-section">
                <div className="hero-container">
                    <div className="hero-row">
                        <h1 className="hero-title">{categoryData?.category?.name}</h1>
                        <div className="hero-type-filter">
                            <div className="category-dropdown-wrapper">
                                <select
                                    value={type}
                                    onChange={(e) => {
                                        const nextType = e.target.value;
                                        skipFetchEffectRef.current = true;
                                        setType(nextType);
                                        setCurrentPage(1);
                                        fetchCategoryData(nextType, 1);
                                    }}
                                    aria-label="Filter content type"
                                >
                                    <option value="">All</option>
                                    <option value="article">Articles</option>
                                    <option value="news">News</option>
                                    <option value="market-insights">Market Insights</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <p className="hero-description">
                        {categoryData?.category?.description}
                    </p>
                </div>
            </section>

            {/* Articles Grid */}
            <section className="articles-grid-section">
                <div className="container">
                    <div className="row">
                        {categoryData?.content?.map((article, index) => {
                            const rawUrl = article?.image?.url;
                            const imageSrc =
                                typeof rawUrl === 'string' && rawUrl.trim() !== '' ? rawUrl.trim() : Energy;

                            return (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="article-card">
                                    <div className="card-image-wrapper">
                                        <Image src={imageSrc} alt={article?.title ?? 'Article'} width={400} height={250} className="card-image" />
                                    </div>
                                    <div className="card-content">
                                        <h2 className="article-title"><Link href={`/${article.type}/${article.slug}`} target='_blank' style={{textDecoration: 'none', color: '#0C0C0C', cursor: 'pointer'}}>{article.title}</Link></h2>
                                        <p className="article-excerpt">{article.excerpt}</p>
                                        <div className="article-meta">
                                            <span>{article.author?.name}</span>
                                            <div className="meta-divider"></div>
                                            <span>{article.created_at}</span>
                                        </div>
                                        <Link href={`/${article.type}/${article.slug}`} target='_blank' style={{textDecoration: 'none', color: '#0C0C0C', cursor: 'pointer'}} className="read-more-link">
                                            Read More
                                            <div className="arrow-circle">
                                                <ChevronRight size={14} />
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {pagination && totalPages > 1 && (
                <div className="pagination-container">
                    <button
                        type="button"
                        className="pag-icon-btn"
                        onClick={() => goToPage(1)}
                        disabled={currentPage <= 1}
                        aria-label="First page"
                    >
                        <ChevronsLeft size={20} strokeWidth={1.75} />
                    </button>
                    <button
                        type="button"
                        className="pag-box-btn pag-box-btn--prev"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {visiblePages.map((page) => (
                        <button
                            key={page}
                            type="button"
                            className={`pag-num ${currentPage === page ? 'is-active' : ''}`}
                            onClick={() => goToPage(page)}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        type="button"
                        className="pag-box-btn pag-box-btn--next"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        aria-label="Next page"
                    >
                        <ChevronRight size={18} />
                    </button>
                    <button
                        type="button"
                        className="pag-icon-btn pag-icon-btn--emphasis"
                        onClick={() => goToPage(totalPages)}
                        disabled={currentPage >= totalPages}
                        aria-label="Last page"
                    >
                        <ChevronsRight size={20} strokeWidth={1.75} />
                    </button>
                </div>
            )}
        </div>
    );
}
